import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import { connect } from 'react-redux';

// components
import PdpRespPreviewMessageBanner from '../PdpRespPreviewMessageBanner/PdpRespPreviewMessageBanner';
import PdpRespPrintHeader from '../PdpRespPrintHeader/PdpRespPrintHeader';
import PdpRespSimilarItemsLanding from '../PdpRespSimilarItems/PdpRespSimilarItemsLanding';
import PdpRespBreadcrumbs from '../PdpRespBreadcrumbs/PdpRespBreadcrumbs';
import PdpRespHeroCarousel from '../PdpRespHeroCarousel/PdpRespHeroCarousel';
import PdpRespHeroCarouselPrint from '../PdpRespHeroCarousel/PdpRespHeroCarouselPrint';
import PdpRespIconBubbles from '../PdpRespIconBubbles/PdpRespIconBubbles';
import PdpRespTitle from '../PdpRespTitle/PdpRespTitle';
import PdpSharedSidebarFavorites from '../PdpSharedSidebarFavorites/PdpSharedSidebarFavorites';
import PdpSharedSidebarPrice from '../PdpSharedSidebarPrice/PdpSharedSidebarPrice';
import PdpRespSidebarButtons from '../PdpRespSidebarButtons/PdpRespSidebarButtons';
import PdpRespSidebarBlurbs from '../PdpRespSidebarBlurbs/PdpRespSidebarBlurbs';
import PdpSharedDescriptionAbout from '../PdpSharedDescriptions/PdpSharedDescriptionAbout';
import PdpSharedDescriptionAdditionalOptions from '../PdpSharedDescriptions/PdpSharedDescriptionAdditionalOptions';
import PdpRespDetails from '../PdpRespDetails/PdpRespDetails';
import PdpRespShippingReturns from '../PdpRespDetails/PdpRespShippingReturns';
import PdpSharedDescriptionAboutCreator from '../PdpSharedDescriptions/PdpSharedDescriptionAboutCreator';
import PdpSharedDescriptionAboutMaker from '../PdpSharedDescriptions/PdpSharedDescriptionAboutMaker';
import PdpRespBuyerAssuranceBox from '../PdpRespBuyerAssuranceBox/PdpRespBuyerAssuranceBox';
import PdpRespSidebarContact from '../PdpRespSidebarContact/PdpRespSidebarContact';
import PdpRespSimilarItemsGridSeller from '../PdpRespSimilarItems/PdpRespSimilarItemsGridSeller';
import PdpRespSimilarItemsGrid from '../PdpRespSimilarItems/PdpRespSimilarItemsGrid';
import PdpRespSeoLinks from '../PdpRespSeoLinks/PdpRespSeoLinks';
import PdpRespDealerDetails from '../PdpRespDealerDetails/PdpRespDealerDetails';
import PdpSharedQuantitySelect from '../PdpSharedButtons/PdpSharedQuantitySelect';
import PdpSharedReminderLoader from '../PdpSharedReminder/PdpSharedReminderLoader';
import PdpSharedRecentHistoryTracker from '../PdpSharedRecentHistoryTracker/PdpSharedRecentHistoryTracker';
import PdpSharedPromoCode from '../PdpSharedPromoCode/PdpSharedPromoCode';
import PdpRespRecentlyViewedItems from '../PdpRespRecentlyViewedItems/PdpRespRecentlyViewedItems';
import PdpSharedRestrictedMaterialsLoader from '../PdpSharedRestrictedMaterials/PdpSharedRestrictedMaterialsLoader';

// callouts
import { CALLOUT_KEYS } from '../PdpRespNotifications/pdpRespNotificationConstants';
import { CALLOUT_KEYS as SHARED_CALLOUT_KEYS } from '../PdpSharedNotifications/pdpSharedNotificationConstants';
import PdpRespNotificationsWrapper from '../PdpRespNotifications/PdpRespNotificationsWrapper/PdpRespNotificationsWrapper';
import PdpRespNotificationSaveItemCalloutLoader from '../PdpRespNotifications/PdpRespNotificationSaveItemCallout/PdpRespNotificationSaveItemCalloutLoader';
import PdpRespNotificationSaveToFolderCalloutLoader from '../PdpRespNotifications/PdpRespNotificationSaveToFolderCallout/PdpRespNotificationSaveToFolderCalloutLoader';
import PdpSharedNotificationMultiplePdpVisitsModalLoader from '../PdpSharedNotifications/PdpSharedNotificationMultiplePdpVisitsModal/PdpSharedNotificationMultiplePdpVisitsModalLoader';

// helpers
import classnames from 'classnames';

// styles
import styles from './PdpRespContainer.scss';

/* eslint-disable react/display-name, react/prop-types */
// disable for function as child of PdpRespNotificationsWrapper

export function PdpRespContainer(props) {
    const {
        pdpPrint,
        currency,
        viewer,
        user,
        isPdpRespPreviewPage,
        item,
        displayInfo,
        sellerItems,
        similarGridItems,
        similarLandingItems,
        vipUserRelationship,
        localDeliveryOptions,
        domesticDeliveryOptions,
        relayVariables,
        relay,
        hasFetchedOnClient,
    } = props;

    return (
        <div>
            {/* Preview Page Message Banner */}
            {isPdpRespPreviewPage && <PdpRespPreviewMessageBanner />}

            {/* Print Header */}
            {relayVariables.isClient && (
                <div className={styles.printShow}>
                    <PdpRespPrintHeader itemId={relayVariables.itemId} />
                </div>
            )}

            {/* Similar Items */}
            <div className={styles.printHide}>
                <PdpRespSimilarItemsLanding
                    relayVariables={{
                        isClient: relayVariables.isClient,
                        showSimilarItems: relayVariables.showSimilarItems,
                    }}
                    item={item}
                    similarItems={similarLandingItems}
                />
            </div>

            {/* Notification reminder loader */}
            <PdpSharedReminderLoader
                relayVariables={{
                    userId: relayVariables.userId,
                    fetchUser: relayVariables.fetchUser,
                    itemId: relayVariables.itemId,
                    isClient: relayVariables.isClient,
                }}
                environment={relay.environment}
                load={!!user}
            />

            {/* PdpBreadcrumbs */}
            <div className={styles.printHide}>
                <PdpRespBreadcrumbs item={item} />
            </div>

            <div className="rowFlex">
                <div className={classnames(styles.topContainer, 'colXs12 colLg7 colXl8')}>
                    {/* Image Carousel */}
                    <div className={styles.printHide}>
                        <PdpRespHeroCarousel
                            itemTitle={item.title || ''}
                            itemImage={item.firstPhotoWebPath || ''}
                            item={item}
                            user={user}
                            userId={relayVariables.userId}
                            itemId={relayVariables.itemId}
                            viewer={viewer || null}
                            fetchFavorites={relayVariables.fetchFavorites}
                            disableZoomModal={isPdpRespPreviewPage}
                            disableRequestMoreImagesModal={isPdpRespPreviewPage}
                        />
                    </div>
                    {/* Stripped Image Carousel (print view) */}
                    {relayVariables.isClient && (
                        <div className={styles.printShow}>
                            <PdpRespHeroCarouselPrint item={item} />
                        </div>
                    )}

                    {/* Social Icons / Print */}
                    <div className={styles.printHide}>
                        <PdpRespIconBubbles
                            relayVariables={{
                                isDesktop: relayVariables.isDesktop,
                            }}
                            item={item}
                            user={user}
                            viewer={viewer}
                        />
                    </div>
                </div>

                {/* Sidebar */}
                <div className={classnames(styles.topContainer, 'colXs12 colLg5 colXl4')}>
                    <div className={styles.sidebarContainer}>
                        <div className="rowFlex">
                            {/* Title */}
                            <div className="colXs12 colLg10">
                                <PdpRespTitle item={item} />
                            </div>
                            {/* Favorite / Folder */}
                            {viewer.isFavoritesEnabled && (
                                <div className={classnames(styles.printHide, 'colXs2')}>
                                    <div className={styles.favoriteCalloutSpacer}>
                                        <PdpRespNotificationsWrapper type={CALLOUT_KEYS.SAVE_ITEM}>
                                            {({ isEnabled }) => (
                                                <PdpRespNotificationSaveItemCalloutLoader
                                                    isEnabled={isEnabled}
                                                />
                                            )}
                                        </PdpRespNotificationsWrapper>
                                    </div>
                                    <div className={styles.folderCalloutSpacer}>
                                        <PdpRespNotificationsWrapper
                                            type={CALLOUT_KEYS.SAVE_TO_FOLDER}
                                        >
                                            {({ isEnabled }) => (
                                                <PdpRespNotificationSaveToFolderCalloutLoader
                                                    isEnabled={isEnabled}
                                                />
                                            )}
                                        </PdpRespNotificationsWrapper>
                                    </div>
                                    <PdpSharedSidebarFavorites
                                        itemTitle={item.title || ''}
                                        itemImage={item.firstPhotoWebPath || ''}
                                        itemId={relayVariables.itemId}
                                        isClient={relayVariables.isClient}
                                        fetchFavorites={relayVariables.fetchFavorites}
                                        environment={relay.environment}
                                        viewer={viewer}
                                        user={user}
                                        wrapperClass={styles.favoritesWrapper}
                                        heartIconWrapperClass={styles.heartIconWrapper}
                                        hasFetchedOnClient={hasFetchedOnClient}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Price */}
                        <div
                            className={classnames({ [styles.printHide]: !pdpPrint.printWithPrice })}
                        >
                            <PdpSharedSidebarPrice
                                currency={currency}
                                item={item}
                                displayInfo={displayInfo}
                            />
                            <PdpSharedPromoCode
                                userId={relayVariables.userId}
                                itemId={relayVariables.itemId}
                                displayInfo={displayInfo}
                            />
                        </div>
                        {/* Item Ctas */}
                        <div className={styles.printHide}>
                            <PdpSharedQuantitySelect item={item} viewer={viewer}>
                                {function quantitySelection(quantitySelected) {
                                    return (
                                        <PdpRespSidebarButtons
                                            currency={currency}
                                            relayVariables={{
                                                userId: relayVariables.userId,
                                                itemId: relayVariables.itemId,
                                                relationshipUserIds:
                                                    relayVariables.relationshipUserIds,
                                                fetchFavorites: relayVariables.fetchFavorites,
                                            }}
                                            isClient={relayVariables.isClient}
                                            user={user}
                                            item={item}
                                            viewer={viewer}
                                            displayInfo={displayInfo}
                                            quantitySelected={quantitySelected}
                                        />
                                    );
                                }}
                            </PdpSharedQuantitySelect>
                        </div>

                        {/* Restricted Materials */}
                        {relayVariables.isClient && (
                            <PdpSharedRestrictedMaterialsLoader
                                environment={relay.environment}
                                itemId={relayVariables.itemId}
                                isClient={relayVariables.isClient}
                            />
                        )}

                        {/* Blurbs */}
                        <PdpRespSidebarBlurbs
                            currency={currency}
                            relayVariables={{
                                userId: relayVariables.userId,
                                itemId: relayVariables.itemId,
                            }}
                            viewer={viewer}
                            item={item}
                            user={user}
                            displayInfo={displayInfo}
                            vipUserRelationship={vipUserRelationship}
                            localDeliveryOptions={localDeliveryOptions}
                            domesticDeliveryOptions={domesticDeliveryOptions}
                        />
                        {/* Questions about this piece? / Contact Dealer */}
                        <div className={styles.printHide}>
                            <PdpRespSidebarContact
                                displayInfo={displayInfo}
                                item={item}
                                user={user}
                                itemId={relayVariables.itemId}
                                userId={relayVariables.userId}
                                isClient={relayVariables.isClient}
                                vipUserRelationship={vipUserRelationship}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className={`rowFlex ${styles.details}`}>
                <div className="colXs12 colXl9">
                    {/* About */}
                    <PdpSharedDescriptionAbout item={item} />
                    {/* Additional Options */}
                    <PdpSharedDescriptionAdditionalOptions item={item} />
                    {/* Details */}
                    <PdpRespDetails currency={currency} item={item} />
                    {/* Shipping and Returns */}
                    <PdpRespShippingReturns
                        currency={currency}
                        relayVariables={{
                            userId: relayVariables.userId,
                            itemId: relayVariables.itemId,
                        }}
                        viewer={viewer}
                        user={user}
                        item={item}
                        localDeliveryOptions={localDeliveryOptions}
                        domesticDeliveryOptions={domesticDeliveryOptions}
                    />
                    {/* Creator Content */}
                    {viewer.isAboutTheMakerEnabled && (
                        <div className={styles.printHide}>
                            <PdpSharedDescriptionAboutCreator item={item} />
                        </div>
                    )}
                    {/* Maker Content */}
                    {viewer.isAboutTheMakerEnabled && (
                        <div className={styles.printHide}>
                            <PdpSharedDescriptionAboutMaker item={item} />
                        </div>
                    )}
                </div>
            </div>

            {/* Contact Dealer Details */}
            <div className={styles.printHide}>
                <PdpRespDealerDetails user={user} item={item} />
            </div>

            {/* More From Seller */}
            <div className={styles.printHide}>
                <PdpRespSimilarItemsGridSeller
                    hasFetchedOnClient={hasFetchedOnClient}
                    relayVariables={{
                        userId: relayVariables.userId,
                        isClient: relayVariables.isClient,
                        fetchFavorites: relayVariables.fetchFavorites,
                    }}
                    item={item}
                    sellerItems={sellerItems}
                    viewer={viewer}
                    currency={currency}
                    user={user}
                />
            </div>
            {/* You May Also Like */}
            <div className={styles.printHide}>
                <PdpRespSimilarItemsGrid
                    hasFetchedOnClient={hasFetchedOnClient}
                    relayVariables={{
                        userId: relayVariables.userId,
                        isClient: relayVariables.isClient,
                        fetchFavorites: relayVariables.fetchFavorites,
                    }}
                    item={item}
                    similarItems={similarGridItems}
                    viewer={viewer}
                    currency={currency}
                    user={user}
                />
            </div>
            {/* Recently Viewed Items */}
            <div className={styles.printHide}>
                <PdpRespRecentlyViewedItems
                    itemId={item.serviceId}
                    isClient={relayVariables.isClient}
                />
            </div>
            {/* Photos - todo (INTL-70: PDP follow on) */}

            {/* More Ways To Browse */}
            <div className={styles.printHide}>
                <PdpRespSeoLinks item={item} viewer={viewer} />
            </div>
            <PdpRespNotificationsWrapper type={SHARED_CALLOUT_KEYS.MULTI_PDP_VISITS}>
                {({ isEnabled }) => (
                    <PdpSharedNotificationMultiplePdpVisitsModalLoader
                        isEnabled={isEnabled && !isPdpRespPreviewPage}
                        itemId={relayVariables.itemId}
                        environment={relay.environment}
                    />
                )}
            </PdpRespNotificationsWrapper>

            {/* Recent History */}
            {!isPdpRespPreviewPage && <PdpSharedRecentHistoryTracker item={item} />}

            {/* Buyer Assurance Box */}
            <div className={styles.printHide}>
                <PdpRespBuyerAssuranceBox user={user} />
            </div>
        </div>
    );
}

PdpRespContainer.propTypes = {
    currency: PropTypes.string,
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        itemId: PropTypes.string.isRequired,
        isClient: PropTypes.bool.isRequired,
        fetchUser: PropTypes.bool.isRequired,
        relationshipUserIds: PropTypes.array.isRequired,
        favoritePages: PropTypes.array,
        fetchFavorites: PropTypes.bool.isRequired,
    }).isRequired,
    relay: PropTypes.object.isRequired,

    // relay response data
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    displayInfo: PropTypes.object,
    sellerItems: PropTypes.object,
    hasFetchedOnClient: PropTypes.bool,
    similarLandingItems: PropTypes.object,
    similarGridItems: PropTypes.object,
    vipUserRelationship: PropTypes.object,
    localDeliveryOptions: PropTypes.object,
    domesticDeliveryOptions: PropTypes.object,

    // from redux
    pdpPrint: PropTypes.shape({
        printWithPrice: PropTypes.bool.isRequired,
    }).isRequired,
};

function mapStateToProps({ pdpPrint, pdpPreviewPage }) {
    return {
        pdpPrint: {
            printWithPrice: pdpPrint.printWithPrice,
        },
        isPdpRespPreviewPage: pdpPreviewPage.isPdpRespPreviewPage,
    };
}

export default createFragmentContainer(connect(mapStateToProps)(PdpRespContainer), {
    viewer: graphql`
        fragment PdpRespContainer_viewer on Viewer {
            ...PdpRespSidebarButtons_viewer
            ...PdpRespSidebarBlurbs_viewer
            ...PdpRespShippingReturns_viewer
            ...PdpRespIconBubbles_viewer
            ...PdpRespSimilarItemsGrid_viewer
            ...PdpRespSimilarItemsGridSeller_viewer
            ...PdpSharedSidebarFavorites_viewer
            ...PdpRespHeroCarousel_viewer
            ...PdpRespSeoLinks_viewer
            isAboutTheMakerEnabled: isEnabledByLocale(featureName: "ABOUT_THE_MAKER")
            isFavoritesEnabled: isEnabledByLocale(featureName: "FAVORITES")
        }
    `,
    user: graphql`
        fragment PdpRespContainer_user on User {
            ...PdpRespSimilarItemsGrid_user
            ...PdpRespSimilarItemsGridSeller_user
            ...PdpRespSidebarButtons_user
            ...PdpRespSidebarBlurbs_user
            ...PdpRespShippingReturns_user
            ...PdpRespBuyerAssuranceBox_user
            ...PdpRespSidebarContact_user
            ...PdpRespHeroCarousel_user
            ...PdpRespIconBubbles_user
            ...PdpRespDealerDetails_user
            ...PdpSharedSidebarFavorites_user
        }
    `,
    item: graphql`
        fragment PdpRespContainer_item on Item {
            serviceId
            firstPhotoWebPath(size: small)
            title
            ...PdpRespSimilarItemsLanding_item
            ...PdpRespBreadcrumbs_item
            ...PdpRespHeroCarousel_item
            ...PdpRespHeroCarouselPrint_item
            ...PdpRespIconBubbles_item
            ...PdpRespTitle_item
            ...PdpSharedSidebarPrice_item
            ...PdpRespSidebarButtons_item
            ...PdpRespSidebarBlurbs_item
            ...PdpSharedDescriptionAbout_item
            ...PdpSharedDescriptionAdditionalOptions_item
            ...PdpRespDetails_item
            ...PdpSharedDescriptionAboutCreator_item
            ...PdpRespShippingReturns_item
            ...PdpSharedDescriptionAboutMaker_item
            ...PdpRespSidebarContact_item
            ...PdpRespSeoLinks_item
            ...PdpRespSimilarItemsGridSeller_item
            ...PdpSharedQuantitySelect_item
            ...PdpRespDealerDetails_item
            ...PdpSharedRecentHistoryTracker_item
        }
    `,
    displayInfo: graphql`
        fragment PdpRespContainer_displayInfo on ItemDisplayInfo {
            ...PdpSharedSidebarPrice_displayInfo
            ...PdpRespSidebarButtons_displayInfo
            ...PdpRespSidebarContact_displayInfo
            ...PdpSharedPromoCode_displayInfo
            ...PdpRespSidebarBlurbs_displayInfo
        }
    `,
    sellerItems: graphql`
        fragment PdpRespContainer_sellerItems on moreLikeThisConnection {
            ...PdpRespSimilarItemsGridSeller_sellerItems
        }
    `,
    similarGridItems: graphql`
        fragment PdpRespContainer_similarGridItems on moreLikeThisConnection {
            ...PdpRespSimilarItemsGrid_similarItems
        }
    `,
    similarLandingItems: graphql`
        fragment PdpRespContainer_similarLandingItems on moreLikeThisConnection {
            ...PdpRespSimilarItemsLanding_similarItems
        }
    `,
    vipUserRelationship: graphql`
        fragment PdpRespContainer_vipUserRelationship on UserRelationship {
            ...PdpRespSidebarBlurbs_vipUserRelationship
            ...PdpRespSidebarContact_vipUserRelationship
        }
    `,
    localDeliveryOptions: graphql`
        fragment PdpRespContainer_localDeliveryOptions on ShipmentQuoteConnection {
            ...PdpRespSidebarBlurbs_localDeliveryOptions
            ...PdpRespShippingReturns_localDeliveryOptions
        }
    `,
    domesticDeliveryOptions: graphql`
        fragment PdpRespContainer_domesticDeliveryOptions on ShipmentQuoteConnection {
            ...PdpRespSidebarBlurbs_domesticDeliveryOptions
            ...PdpRespShippingReturns_domesticDeliveryOptions
        }
    `,
});
